<template>
  <div>
    <div class="row">
      <div class="col-12">
        <Card cardClasses="border-0 shadow-sm mb-5">

          <div v-if="loading" class="text-center py-5">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
          </div>

          <div v-else-if="!loanApplication" class="text-center py-5">
            <h5>Loan application not found</h5>
            <button class="btn btn-primary mt-3" @click="goBack">
              <vue-feather type="arrow-left" size="16" class="me-1"></vue-feather>
              Back to Loans
            </button>
          </div>

          <div v-else>
            <div class="loan-profile mb-5">
              <div class="row mb-4">
                <div class="col-md-8">
                  <div class="d-flex align-items-center">
                    <div class="bg-primary-light p-3 rounded-2 me-3">
                      <vue-feather type="dollar-sign" class="d-flex justify-content-center align-content-center" stroke="#652aa3" size="24"></vue-feather>
                    </div>
                    <h3 class="mb-0 fw-bold">Loan Application Details</h3>
                  </div>
                </div>
                <div class="col-md-4">
                  <div class="d-flex justify-content-end gap-3">
                    <badge class="ms-2 badge-lg" :type="getStatusBadgeType(loanApplication.status)">
                      <vue-feather :type="getIconByStatus(loanApplication.status)" size="20" class="me-1 pt-1"></vue-feather>
                      <span class="fs-4">{{ capitalizeFirstLetter(loanApplication.status) }}</span>
                    </badge>
                    <button type="button" class="btn btn-outline-dark" @click="goBack">
                      <vue-feather type="arrow-left" size="16"></vue-feather>
                      Back
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div class="mb-5">
              <h5 class="mb-3 text-primary">
                <vue-feather type="user" size="18" class="me-2"></vue-feather>
                Employee Information
              </h5>
              <div class="row g-3">
                <div class="col-md-3">
                  <div class="d-flex align-items-center">
                    <vue-feather type="user" size="16" class="text-primary me-2"></vue-feather>
                    <div>
                      <div class="text-muted small">Name</div>
                      <div class="fw-medium">{{ loanApplication.employee.first_name }} {{ loanApplication.employee.last_name }}</div>
                    </div>
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="d-flex align-items-center">
                    <vue-feather type="hash" size="16" class="text-primary me-2"></vue-feather>
                    <div>
                      <div class="text-muted small">Employee No</div>
                      <div class="fw-medium">{{ loanApplication.employee.employee_no }}</div>
                    </div>
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="d-flex align-items-center">
                    <vue-feather type="layers" size="16" class="text-primary me-2"></vue-feather>
                    <div>
                      <div class="text-muted small">Department</div>
                      <div class="fw-medium">{{ loanApplication.employee.department.name || 'N/A' }}</div>
                    </div>
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="d-flex align-items-center">
                    <vue-feather type="briefcase" size="16" class="text-primary me-2"></vue-feather>
                    <div>
                      <div class="text-muted small">Designation</div>
                      <div class="fw-medium">{{ loanApplication.employee.designation.name || 'N/A' }}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <hr>

            <div class="mb-5">
              <h5 class="mb-3 text-primary">
                <vue-feather type="file-text" size="18" class="me-2"></vue-feather>
                Loan Information
              </h5>
              <div class="row g-3">
                <div class="col-md-3">
                  <div class="d-flex align-items-center">
                    <vue-feather type="dollar-sign" size="16" class="text-primary me-2"></vue-feather>
                    <div>
                      <div class="text-muted small">Principal Amount</div>
                      <div class="fw-medium">{{ formatNumber(loanApplication.amount) }}</div>
                    </div>
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="d-flex align-items-center">
                    <vue-feather type="clock" size="16" class="text-primary me-2"></vue-feather>
                    <div>
                      <div class="text-muted small">Duration</div>
                      <div class="fw-medium">{{ loanApplication.term_months + ' Month(s)' }}</div>
                    </div>
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="d-flex align-items-center">
                    <vue-feather type="percent" size="16" class="text-primary me-2"></vue-feather>
                    <div>
                      <div class="text-muted small">Total Interest</div>
                      <div class="fw-medium">{{ formatNumber(loanApplication.interest_amount) }}</div>
                    </div>
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="d-flex align-items-center">
                    <vue-feather type="repeat" size="16" class="text-primary me-2"></vue-feather>
                    <div>
                      <div class="text-muted small">Monthly Installment</div>
                      <div class="fw-medium">{{ formatNumber(((loanApplication.amount + loanApplication.interest_amount) / loanApplication.term_months)) }}</div>
                    </div>
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="d-flex align-items-center">
                    <vue-feather type="bar-chart-2" size="16" class="text-primary me-2"></vue-feather>
                    <div>
                      <div class="text-muted small">Maximum Amount</div>
                      <div class="fw-medium">{{ formatNumber(loanApplication.max_amount) }}</div>
                    </div>
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="d-flex align-items-center">
                    <vue-feather type="calendar" size="16" class="text-primary me-2"></vue-feather>
                    <div>
                      <div class="text-muted small">Applied On</div>
                      <div class="fw-medium">{{ formatDate(loanApplication.created_at) }}</div>
                    </div>
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="d-flex align-items-center">
                    <vue-feather type="target" size="16" class="text-primary me-2"></vue-feather>
                    <div>
                      <div class="text-muted small">Purpose</div>
                      <div class="fw-medium">{{ loanApplication.purpose || 'N/A' }}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="loanApplication.status === 'approved' || loanApplication.status === 'rejected'" class="mb-4">
              <hr>
              <h5 class="mb-3 text-primary">
                <vue-feather type="check-circle" size="18" class="me-2"></vue-feather>
                {{ loanApplication.status === 'approved' ? 'Approval' : 'Rejection' }} Information
              </h5>
              <div class="row g-3">
                <div class="col-md-4">
                  <div class="d-flex align-items-center">
                    <vue-feather type="user" size="16" class="text-primary me-2"></vue-feather>
                    <div>
                      <div class="text-muted small">{{ loanApplication.status === 'approved' ? 'Approved' : 'Rejected' }} By</div>
                      <div class="fw-medium">
                        {{ loanApplication.approver?.first_name + ' ' + loanApplication.approver?.last_name }}
                      </div>
                    </div>
                  </div>
                </div>
                <div class="col-md-4">
                  <div class="d-flex align-items-center">
                    <vue-feather type="calendar" size="16" class="text-primary me-2"></vue-feather>
                    <div>
                      <div class="text-muted small">{{ loanApplication.status === 'approved' ? 'Approved' : 'Rejected' }} On</div>
                      <div class="fw-medium">
                        {{ formatDate(loanApplication.approved_at) }}
                      </div>
                    </div>
                  </div>
                </div>
                <div v-if="loanApplication.status === 'rejected'" class="col-md-4">
                  <div class="d-flex align-items-center">
                    <vue-feather type="alert-circle" size="16" class="text-primary me-2"></vue-feather>
                    <div>
                      <div class="text-muted small">Rejection Reason</div>
                      <div class="fw-medium">{{ loanApplication.rejection_reason || 'N/A' }}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Guarantors Section -->
            <div class="mb-5">
              <hr>
              <div class="d-flex justify-content-between align-items-center mb-4">
                <h5 class="mb-0 text-primary">
                  <vue-feather type="users" size="18" class="me-2"></vue-feather>
                  Guarantors
                </h5>
                <button v-if="loanApplication.status === 'pending' && isOwner" class="btn btn-sm btn-outline-primary" @click="openAddGuarantorModal">
                  <vue-feather type="user-plus" size="14" class="me-1"></vue-feather>
                  Add Guarantor
                </button>
              </div>

              <div v-if="loanApplication.guarantors.length === 0" class="text-center py-4">
                <vue-feather type="users" size="32" class="mb-2 text-muted"></vue-feather>
                <p class="mb-0 text-muted">No guarantors found for this loan application.</p>
              </div>

              <div v-else class="table-responsive">
                <table class="table table-hover align-middle">
                  <thead class="table-light">
                    <tr>
                      <th scope="col" class="fw-semibold">Employee No</th>
                      <th scope="col" class="fw-semibold">Name</th>
                      <th scope="col" class="fw-semibold">Department</th>
                      <th scope="col" class="fw-semibold">Status</th>
                      <th scope="col" class="fw-semibold">Requested On</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="guarantor in loanApplication.guarantors" :key="guarantor.id">
                      <td>{{ guarantor.employee.employee_no }}</td>
                      <td>
                        <div class="fw-medium">{{ guarantor.employee.first_name }} {{ guarantor.employee.last_name }}</div>
                      </td>
                      <td>{{ guarantor.employee.department.name }}</td>
                      <td>
                        <badge :type="getStatusBadgeType(guarantor.status)">
                          {{ capitalizeFirstLetter(guarantor.status) }}
                        </badge>
                      </td>
                      <td>{{ formatDate(guarantor.created_at) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div class="d-flex justify-content-end mt-4">
              <button v-if="isPrimary && loanApplication.status === 'pending' && guarantorsApproval(loanApplication)" class="btn btn-success me-2" @click="openApproveModal">
                <vue-feather type="check" size="16" class="me-1"></vue-feather>
                Approve
              </button>
              <button v-if="isPrimary && loanApplication.status === 'pending' && guarantorsApproval(loanApplication)" class="btn btn-danger me-2" @click="openRejectModal">
                <vue-feather type="x" size="16" class="me-1"></vue-feather>
                Reject
              </button>
              <button type="button" class="btn btn-outline-dark" @click="goBack">
                <vue-feather type="arrow-left" size="16" class="me-1"></vue-feather>
                Back
              </button>
            </div>
          </div>
        </Card>
      </div>
    </div>

    <!-- Reject Loan Application Modal -->
    <Modal
        :open="showRejectModal"
        @closed="closeRejectModal"
    >
      <div class="modal-header border-0">
        <h5 class="modal-title m-0 d-flex align-items-center">
          Reject Loan Application
        </h5>
        <button @click="closeRejectModal" type="button" class="btn-close outline-none" data-dismiss="modal" aria-label="Close">
        </button>
      </div>
      <div class="modal-body">
        <form @submit.prevent="rejectLoanApplication">
          <div class="mb-3">
            <label for="rejection_reason" class="form-label">Reason for Rejection <span class="text-danger">*</span></label>
            <textarea class="form-control" id="rejection_reason" v-model="rejectForm.rejection_reason" rows="3" required></textarea>
          </div>
          <div class="d-flex justify-content-end">
            <button type="button" class="btn btn-outline-secondary me-2" @click="closeRejectModal">Cancel</button>
            <button type="submit" class="btn btn-danger" :disabled="saving">
              <span v-if="saving" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
              Reject
            </button>
          </div>
        </form>
      </div>
    </Modal>

    <!-- Approve Loan Application Modal -->
    <Modal
        :open="showApproveModal"
        @closed="closeApproveModal"
    >
      <div class="modal-header border-0">
        <h5 class="modal-title m-0 d-flex align-items-center">
          Approve Loan Application
        </h5>
        <button @click="closeApproveModal" type="button" class="btn-close outline-none" data-dismiss="modal" aria-label="Close">
        </button>
      </div>
      <div class="modal-body">
        <form @submit.prevent="approveLoanApplication">
          <div class="mb-3">
            <label for="comments" class="form-label">Comments (Optional)</label>
            <textarea class="form-control" id="comments" v-model="approveForm.comments" rows="3"></textarea>
          </div>
          <div class="d-flex justify-content-end">
            <button type="button" class="btn btn-outline-secondary me-2" @click="closeApproveModal">Cancel</button>
            <button type="submit" class="btn btn-success" :disabled="saving">
              <span v-if="saving" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
              Approve
            </button>
          </div>
        </form>
      </div>
    </Modal>

    <!-- Add Guarantor Modal -->
    <Modal
        :open="showAddGuarantorModal"
        @closed="closeAddGuarantorModal"
    >
      <div class="modal-header border-0">
        <h5 class="modal-title m-0 d-flex align-items-center">
          <vue-feather type="user-plus" size="18" class="me-2"></vue-feather>
          Add Guarantor
        </h5>
        <button @click="closeAddGuarantorModal" type="button" class="btn-close outline-none" data-dismiss="modal" aria-label="Close">
        </button>
      </div>
      <div class="modal-body">
        <form @submit.prevent="addGuarantor">
          <div class="mb-3">
            <label for="employee_id" class="form-label">Select Employee <span class="text-danger">*</span></label>
            <v-select
                label="name"
                v-model="guarantorForm.employee_id"
                :reduce="employee => employee.id"
                :options="employees"
                class="style-chooser"
                placeholder="Type to search"
                @option:selected="guarantorChecks"
            ></v-select>
          </div>
          <div class="d-flex justify-content-end">
            <button type="button" class="btn btn-outline-secondary me-2" @click="closeAddGuarantorModal">Cancel</button>
            <button type="submit" class="btn btn-primary" :disabled="saving">
              <span v-if="saving" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
              Add Guarantor
            </button>
          </div>
        </form>
      </div>
    </Modal>
  </div>
</template>

<script>
import Card from "@/components/card/Card.vue";
import Modal from "@/components/modal/Modal.vue";
import Badge from "@/components/badge/Badge.vue";
import formatDate from "@/utils/formatDate";
import formatNumber from "@/utils/formatNumber";
import loanService from "@/api/loanService";
import notify from "@/utils/notify";
import { decrypt } from "@/utils/crypto";
import selectSearch from "@/utils/selectSearch";

export default {
  name: "LoanDetails",
  components: {
    Card,
    Modal,
    Badge
  },
  data() {
    return {
      loading: true,
      saving: false,
      loadingEmployees: false,
      loanApplication: null,
      employees: [],
      profile: null,
      showRejectModal: false,
      showApproveModal: false,
      showAddGuarantorModal: false,
      rejectForm: {
        rejection_reason: ''
      },
      approveForm: {
        comments: ''
      },
      guarantorForm: {
        employee_id: '',
        loan_id: ''
      }
    };
  },
  methods: {
    formatDate,
    formatNumber,
    capitalizeFirstLetter(string) {
      return string.charAt(0).toUpperCase() + string.slice(1);
    },
    getStatusBadgeType(status) {
      switch (status) {
        case 'approved':
          return 'success';
        case 'rejected':
          return 'danger';
        case 'pending':
          return 'warning';
        default:
          return 'secondary';
      }
    },
    getIconByStatus(status) {
      switch (status) {
        case 'approved':
          return 'check-circle';
        case 'rejected':
          return 'x-circle';
        case 'pending':
          return 'clock';
        default:
          return 'help-circle';
      }
    },
    goBack() {
      this.$router.push('/my-loans');
    },
    async fetchLoanApplication() {
      try {
        this.loading = true;
        this.loanApplication = await loanService.getLoanApplication(this.$route.params.id);
      } catch (error) {
        notify.alert.error(error.response?.data?.message || 'Failed to fetch loan application');
        console.error(error);
      } finally {
        this.loading = false;
      }
    },
    async fetchEmployees() {
      this.employees = await selectSearch.allEmployees()
    },
    guarantorChecks() {
      if (!this.guarantorForm.employee_id) return;

      const selectedId = this.guarantorForm.employee_id;

      // Duplicate in existing guarantors
      if (this.loanApplication.guarantors && this.loanApplication.guarantors.some(g => g.employee.id === selectedId)) {
        notify.alert.error('This employee is already a guarantor for this loan');
        this.guarantorForm.employee_id = '';
        return;
      }

      // Self-guarantor not allowed
      if (selectedId === this.loanApplication.employee_id) {
        notify.alert.error('Loan applicant cannot be their own guarantor');
        this.guarantorForm.employee_id = '';
        return;
      }

      // Salary eligibility check
      const guarantorEmp = this.employees.find(emp => emp.id === selectedId);
      const applicantSalary = this.loanApplication.employee?.salary || 0;
      if (guarantorEmp && guarantorEmp.salary < applicantSalary * 0.4) {
        notify.alert.error("Selected guarantor's salary is not sufficient for this loan");
        this.guarantorForm.employee_id = '';
      }

      // Check if guarantor status is active
      if (guarantorEmp && guarantorEmp.status !== 'active') {
        notify.alert.error('Selected guarantor is not active');
        this.guarantorForm.employee_id = '';
      }
    },
    openRejectModal() {
      this.rejectForm.rejection_reason = '';
      this.showRejectModal = true;
    },
    closeRejectModal() {
      this.showRejectModal = false;
    },
    openApproveModal() {
      this.approveForm.comments = '';
      this.showApproveModal = true;
    },
    closeApproveModal() {
      this.showApproveModal = false;
    },
    openAddGuarantorModal() {
      this.guarantorForm = {
        employee_id: '',
        loan_id: this.loanApplication.id
      };
      this.fetchEmployees();
      this.showAddGuarantorModal = true;
    },
    closeAddGuarantorModal() {
      this.showAddGuarantorModal = false;
    },
    async addGuarantor() {
      if (!this.guarantorForm.employee_id) {
        notify.alert.error('Please select an employee');
        return;
      }

      try {
        this.saving = true;
        let guarantor = await loanService.addGuarantor(this.guarantorForm);
          if (guarantor) {
            this.closeAddGuarantorModal();
            await this.fetchLoanApplication();
          }
      } catch (error) {
        notify.alert.error(error.response?.data?.message || 'Failed to add guarantor');
        console.error(error);
      } finally {
        this.saving = false;
      }
    },
    async rejectLoanApplication() {
      if (!this.rejectForm.rejection_reason) {
        notify.alert.error('Please provide a reason for rejection');
        return;
      }

      try {
        this.saving = true;
        await loanService.rejectLoanApplication(this.loanApplication.id, this.rejectForm);
        this.closeRejectModal();
        await this.fetchLoanApplication();
      } catch (error) {
        notify.alert.error(error.response?.data?.message || 'Failed to reject loan application');
        console.error(error);
      } finally {
        this.saving = false;
      }
    },
    async approveLoanApplication() {
      try {
        this.saving = true;
        await loanService.approveLoanApplication(this.loanApplication.id, this.approveForm);
        this.closeApproveModal();
        await this.fetchLoanApplication();
      } catch (error) {
        notify.alert.error(error.response?.data?.message || 'Failed to approve loan application');
        console.error(error);
      } finally {
        this.saving = false;
      }
    },
    guarantorsApproval(application) {
      return application.guarantors && application.guarantors.filter(g => g.status === 'approved').length >= 2
    }
  },
  computed: {
    isPrimary() {
      return this.profile && this.profile.role === 'primary' || this.profile.role === 'super-admin';
    },
    isOwner() {
      return this.profile.employee_id === this.loanApplication.employee_id;
    }
  },
  async created() {
    this.profile = JSON.parse(decrypt(sessionStorage.getItem('profile')));
    await this.fetchLoanApplication();
  }
};
</script>
